import React from 'react';
import { useIntl } from 'react-intl';
import { Upload, Select } from 'antd';
import {
  UploadOutlined,
  FileTextOutlined,
  DeleteOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import Papa from 'papaparse';
import { CSVLink } from 'react-csv';
import { BulkActionConfig, IdentifierKey } from '../registry';
import { makeT } from '../messages';

// Same header as the admin users export (USERS_Table_*.csv) with one
// shape-representative row, so users can download, edit, and re-upload
// without guessing column names.
const TEMPLATE_HEADER = ['userCode', 'userId', 'merchantReference'];
const TEMPLATE_ROW = ['yurytw86', '6a0eb576744a42190868dd79', '59151907'];
const TEMPLATE_DATA = [TEMPLATE_HEADER, TEMPLATE_ROW];

const { Dragger } = Upload;
const { Option } = Select;

export type ParsedCsv = {
  fileName: string;
  sizeKB: number;
  // original uploaded data preserved so matched/unmatched downloads can
  // mirror the source file (all columns + header), not just the id column
  header: string[] | null;
  rows: string[][];
};

type Props = {
  config: BulkActionConfig;
  parsed: ParsedCsv | null;
  setParsed: (p: ParsedCsv | null) => void;
  identifierKey: IdentifierKey;
  setIdentifierKey: (k: IdentifierKey) => void;
  // Derived in the parent from (parsed, identifierKey). colIndex === -1 when
  // the selected filter has no matching column in the CSV header.
  selection: { colIndex: number; values: string[] };
};

const normalize = (s: string) =>
  String(s || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]/g, '_');

const UploadStep: React.FC<Props> = ({
  config,
  parsed,
  setParsed,
  identifierKey,
  setIdentifierKey,
  selection,
}) => {
  const intl = useIntl();
  const t = makeT(intl);

  const handleFile = (file: File) => {
    Papa.parse(file as any, {
      skipEmptyLines: true,
      complete: (results: any) => {
        const rows: string[][] = (results?.data ?? []).filter(
          (r: any[]) => r && r.length > 0
        );
        if (rows.length === 0) {
          setParsed(null);
          return;
        }
        // Treat row 0 as a header when any of its cells matches a known
        // alias from any filter target — so multi-column CSVs like
        // `name,user_id` are recognised even though cell 0 is unrelated.
        const isHeader = rows[0].some((cell) => {
          const norm = normalize(cell);
          return config.idTargets.some((t) =>
            t.csvHeaderAliases.includes(norm)
          );
        });
        const dataRows = isHeader ? rows.slice(1) : rows;
        setParsed({
          fileName: (file as any).name,
          sizeKB: Math.round(((file as any).size / 1024) * 10) / 10,
          header: isHeader ? rows[0] : null,
          rows: dataRows,
        });
      },
    });
  };

  const uploadProps = {
    name: 'file',
    accept: '.csv',
    multiple: false,
    showUploadList: false,
    customRequest: (options: any) => options.onSuccess(),
    beforeUpload: (file: File) => {
      handleFile(file);
      return false;
    },
  };

  const currentTarget = config.idTargets.find((tg) => tg.key === identifierKey);
  const filterLabel = currentTarget ? t(currentTarget.labelId) : identifierKey;
  const matchedHeader =
    parsed && selection.colIndex >= 0 && parsed.header
      ? parsed.header[selection.colIndex]
      : null;
  const columnMissing = !!parsed && selection.colIndex === -1;

  return (
    <div>
      {!parsed ? (
        <>
          <Dragger {...uploadProps} className="ba-dropzone-wrap">
            <div className="ba-dropzone">
              <UploadOutlined />
              <div className="ba-dropzone-title">
                {t('fleetadmin.bulk.upload.dropzone.title')}
              </div>
              <div className="ba-dropzone-hint">
                {t('fleetadmin.bulk.upload.dropzone.hint')}
              </div>
            </div>
          </Dragger>
          <div className="ba-template-row">
            <CSVLink
              data={TEMPLATE_DATA}
              filename="bulk-archive-template.csv"
              className="ba-csv-link"
              data-testid="BulkArchive-downloadTemplate"
            >
              <DownloadOutlined />{' '}
              {t('fleetadmin.bulk.upload.downloadTemplate')}
            </CSVLink>
          </div>
        </>
      ) : (
        <>
          <div className="ba-file-chip">
            <FileTextOutlined style={{ fontSize: 20, color: '#0AB735' }} />
            <div>
              <div className="ba-file-name">{parsed.fileName} ✓</div>
              <div className="ba-file-meta">
                {t('fleetadmin.bulk.upload.fileMeta', {
                  count: selection.values.length,
                  size: parsed.sizeKB,
                })}
              </div>
            </div>
            <DeleteOutlined
              data-testid="BulkArchive-removeFile"
              className="ba-file-remove"
              onClick={() => setParsed(null)}
            />
          </div>

          <div className="ba-stat-grid">
            <div className="ba-stat">
              <div className="ba-stat-label">
                {t('fleetadmin.bulk.upload.detectedRows')}
              </div>
              <div className="ba-stat-value">{selection.values.length}</div>
            </div>
            <div className="ba-stat">
              <div className="ba-stat-label">
                {t('fleetadmin.bulk.upload.matchedColumn')}
              </div>
              <div
                className="ba-stat-value"
                style={{ fontSize: 18, fontFamily: 'monospace' }}
              >
                {matchedHeader ?? t('fleetadmin.bulk.upload.noColumn')}
              </div>
            </div>
          </div>

          <div className="ba-label">{t('fleetadmin.bulk.upload.filterBy')}</div>
          <Select
            data-testid="BulkArchive-identifierSelect"
            value={identifierKey}
            style={{ width: '100%' }}
            onChange={(v) => setIdentifierKey(v as IdentifierKey)}
          >
            {config.idTargets.map((target) => (
              <Option key={target.key} value={target.key}>
                {t(target.labelId)}
              </Option>
            ))}
          </Select>

          {columnMissing && (
            <div
              className="ba-column-error"
              data-testid="BulkArchive-columnMissing"
            >
              {t('fleetadmin.bulk.upload.columnMissing', {
                filter: filterLabel,
                aliases: (currentTarget?.csvHeaderAliases ?? []).join(', '),
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UploadStep;
